import { useState } from 'react';
import { Icon } from '../../icons/Icon';
import { Button, Notice } from '../../ui/controls';
import { OtpInput } from './OtpInput';
import { CopyRow, useCopy } from './parts';
import { QrCode } from './QrCode';

/** "JBSWY3DPEHPK3PXP" → "JBSW Y3DP EHPK 3PXP" */
export function groupSecret(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * TOTP enrollment (owner/admin first login): a QR code of the otpauth link
 * and the shared secret for manual entry in an authenticator app, then the
 * first 6-digit code.
 */
export function TotpSetup({
  secret,
  otpauthUrl,
  busy,
  onCode,
  resetKey,
}: {
  secret: string;
  otpauthUrl: string;
  busy: boolean;
  onCode: (code: string) => void;
  resetKey: number;
}) {
  const copy = useCopy();
  return (
    <>
      <div className="verify">
        <div className="vf-co">
          <span className="srv" style={{ background: 'var(--blue-soft)', color: 'var(--blue-ink)', boxShadow: 'none' }}>
            <Icon name="key" />
          </span>
          <div>
            <b>인증 앱에 계정 추가</b>
            <span>인증 앱으로 QR 코드를 찍으세요. 찍을 수 없으면 ‘설정 키 입력’을 고른 뒤 아래 키를 입력하세요. QR 코드와 키는 이 화면에서만 보여요.</span>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <QrCode value={otpauthUrl} label="인증 앱에 추가할 QR 코드" />
        </div>
        <CopyRow text={secret.replace(/\s+/g, '')} display={groupSecret(secret)} label="키 복사" />
        <button type="button" className="linkbtn" style={{ alignSelf: 'flex-start' }} onClick={() => void copy(otpauthUrl, 'otpauth 링크를 복사했어요')}>
          인증 앱 링크(otpauth) 복사
        </button>
      </div>
      <div>
        <span className="label">인증 앱에 표시된 6자리 코드</span>
        <OtpInput key={resetKey} onComplete={onCode} disabled={busy} autoFocus />
      </div>
    </>
  );
}

/** One-time display of recovery codes; the user must confirm they stored them. */
export function RecoveryCodes({ codes, onContinue, continueLabel }: { codes: readonly string[]; onContinue: () => void; continueLabel: string }) {
  const copy = useCopy();
  const [stored, setStored] = useState(false);
  return (
    <>
      <Notice tone="warn" icon="key">
        복구 코드는 지금 한 번만 보여요. 인증 앱을 쓸 수 없을 때 로그인에 필요하니 안전한 곳에 보관하세요. 코드마다 한 번씩만 쓸 수 있어요.
      </Notice>
      <ul
        aria-label="복구 코드"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))',
          gap: 8,
        }}
      >
        {codes.map((code) => (
          <li
            key={code}
            style={{
              fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
              fontSize: 14,
              fontWeight: 700,
              textAlign: 'center',
              padding: '9px 10px',
              borderRadius: 14,
              background: 'var(--fill)',
              border: '1px solid var(--line)',
            }}
          >
            {code}
          </li>
        ))}
      </ul>
      <Button icon="copy" onClick={() => void copy(codes.join('\n'), '복구 코드를 복사했어요')}>
        복구 코드 복사
      </Button>
      <label className="check">
        <input type="checkbox" checked={stored} onChange={(event) => setStored(event.target.checked)} />
        <span>복구 코드를 안전한 곳에 보관했어요</span>
      </label>
      <Button variant="primary" size="lg" block disabled={!stored} onClick={onContinue}>
        {continueLabel}
      </Button>
    </>
  );
}
